import { get } from 'lodash';
import { PrimaryButton, Spinner, SpinnerSize, Text, DialogType } from 'office-ui-fabric-react';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { fetchUser, deleteUser } from '../../actions/users';
import Main from '../../components/Main';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import PageTitle from '../../components/PageTitle';
import SavingIndicator from '../../components/SavingIndicator';
import TextButton from '../../components/TextButton';
import useAPI from '../../hooks/useAPI';
import { showFlashMessage } from '../../actions/ui';
import { impersonateUser } from '../../utils/auth';

import { deleteUserContent as dialogContent } from '../../constants';
import CreateEditUserForm from '../../components/CreateEditUserForm';
import DeleteDialog from '../../components/DeleteDialog';
import styles from './CreateEditUser.style';

const CreateEditUser = () => {
  const { oid } = useParams();
  const { pathname, state: locationState } = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const history = useHistory();
  const ref = useRef();
  const dispatch = useDispatch();

  let isLoading = false;
  let error = null;
  let user = null;
  if (oid) {
    ({ isLoading, error } = useAPI(() => fetchUser(oid), [oid]));
    user = useSelector(state => get(state, `entities.users.${oid}`, null));
  }

  const errorDetail = useMemo(() => {
    if (!error) {
      return null;
    }
    let title = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';
    if (error.status === 404) {
      message = 'Requested user does not exist. Please try a different oid.';
      title = 'User Not Found';
    }
    return {
      title,
      message,
    };
  }, [error]);

  const takeControl = async () => {
    await impersonateUser(user);
    window.location.replace('/');
  };

  useEffect(() => {
    if (locationState) {
      setIsDialogVisible(locationState.delete);
    } else {
      setIsDialogVisible(false);
    }
  }, [locationState]);

  useEffect(() => {
    if (pathname) {
      setIsDialogVisible(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (locationState?.impersonate && user) {
      takeControl();
    }
  }, [locationState, user]);

  if (isLoading) {
    return (
      <Spinner
        data-testid="createEditUserSpinner"
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
      />
    );
  }

  if (errorDetail) {
    return (
      <>
        <PageTitle>{errorDetail.title}</PageTitle>
        <Main>
          <Text>{errorDetail.message}</Text>
        </Main>
      </>
    );
  }

  const handleCancel = () => {
    history.goBack();
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    await ref.current.submitForm();
    setIsSubmitting(false);
  };

  const handleSuccess = async newOid => {
    setIsSubmitting(false);
    if (user) {
      dispatch(
        showFlashMessage('User updated successfully', {
          type: 'success',
          duration: 3000,
        }),
      );
    } else {
      window.location.replace(`/permissions/users/${newOid}`);
    }
  };

  const handleFailure = () => {
    setIsSubmitting(false);
  };

  const handleDismiss = () => {
    if (!isRemoving) {
      history.replace({ state: null });
    }
  };

  const handleDialogSubmit = async () => {
    setIsRemoving(true);
    const result = await dispatch(deleteUser(oid, user.role));
    setIsRemoving(false);
    setIsDialogVisible(false);
    if (result && result.error) {
      dispatch(showFlashMessage(result.payload?.message, { type: 'error' }));
    } else if (result && !result.error) {
      history.push({ pathname: '/permissions/users' });
      dispatch(showFlashMessage('User successfully deleted', { type: 'success' }));
    }
  };

  const showSaveButton = () => {
    if (!user) {
      // Case add
      return true;
    }

    return user && user.role === 'msft';
  };

  const pageTitle = () => {
    if (user) {
      return user.role === 'msft' ? 'Edit User' : 'User Details';
    }
    return 'Create User';
  };

  return (
    <>
      <PageTitle>{pageTitle()}</PageTitle>

      <Main>
        <Module>
          <ModuleContent>
            <CreateEditUserForm
              user={user}
              onSuccess={handleSuccess}
              onFailure={handleFailure}
              ref={ref}
            />
          </ModuleContent>
        </Module>

        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          {showSaveButton() && (
            <>
              {!isSubmitting ? (
                <PrimaryButton data-testid="save-user" onClick={handleSave} disabled={isSubmitting}>
                  Save
                </PrimaryButton>
              ) : (
                <SavingIndicator />
              )}
            </>
          )}
        </div>
      </Main>
      <DeleteDialog
        onDismiss={handleDismiss}
        onSubmit={handleDialogSubmit}
        isRemoving={isRemoving}
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
          title: dialogContent.title,
          closeButtonAriaLabel: 'Close',
        }}
        dialogBodyContent={{
          content: `${dialogContent.content}`,
          buttonText: dialogContent.button,
          showCancelButton: dialogContent.showCancelButton,
        }}
      />
    </>
  );
};

export default CreateEditUser;
